import { useState, useEffect, useCallback } from "react";
import {
  Shield, Building2, RefreshCw, Save,
  ChevronDown, ChevronUp, Settings, Eye, EyeOff,
  Info, Monitor, Clock, Key,
  Brain, Phone, Smartphone, MessageCircle, Table2, HardDrive,
  Mail, Receipt, Palette, Send, AudioLines, Mic, Video, Globe, AlertTriangle
} from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Sections du menu Super Batard configurables pour les tenants
const CONFIGURABLE_SECTIONS = [
  {
    id: "supervision",
    label: "Supervision & Direction",
    icon: "👑",
    items: [
      { id: "direction", label: "Pole Direction SAN-IA" },
      { id: "pdg-notifications", label: "Notifications PDG" },
      { id: "agents-conversations", label: "Conversations IA" },
      { id: "connection-history", label: "Historique Connexions" },
      { id: "invoices-validation", label: "Factures a valider" },
    ]
  },
  {
    id: "societe",
    label: "Ma Societe",
    icon: "🏠",
    items: [
      { id: "societes", label: "Gestion Societes" },
      { id: "users", label: "Gestion des acces" },
      { id: "teams", label: "Gestion des equipes" },
      { id: "amo-mar", label: "Gestion AMO / MAR" },
      { id: "dashboards-manager", label: "Ordre Dashboards" },
    ]
  },
  {
    id: "catalogue",
    label: "Catalogue & Produits",
    icon: "📦",
    items: [
      { id: "catalogue-devis", label: "Catalogue Devis" },
      { id: "tarifs-reno-config", label: "Tarifs Reno Energie" },
      { id: "elements-declenchement", label: "Elements de Declenchement" },
    ]
  },
  {
    id: "configuration",
    label: "Configuration",
    icon: "⚙️",
    items: [
      { id: "dashboard-config", label: "Config Globale" },
      { id: "pipeline-config", label: "Config Pipeline" },
      { id: "pipeline-workflow", label: "Workflow" },
      { id: "client-fields-config", label: "Champs Fiche Client" },
      { id: "dynamic-tabs-config", label: "Selections Dynamiques" },
      { id: "payment-conditions-config", label: "Conditions Reglement" },
      { id: "sms-config", label: "Config SMS" },
      { id: "telepro-config", label: "Config Telepro" },
      { id: "formulaires-mapping", label: "Formulaires (Mappings)" },
      { id: "widgets-registry", label: "Widgets" },
      { id: "lead-sources-config", label: "Sources de Lead" },
      { id: "statuts-qualification-config", label: "Statuts Qualification" },
      { id: "integrations", label: "Integrations & Cles" },
    ]
  },
  {
    id: "ia",
    label: "IA",
    icon: "🤖",
    items: [
      { id: "ai", label: "Configuration IA" },
      { id: "prompts", label: "Prompts IA" },
      { id: "agent-directives", label: "Directives Agent" },
      { id: "ia-permissions", label: "Permissions IA" },
      { id: "sania-learning", label: "Apprentissage SAN-IA" },
      { id: "audit-ia", label: "Audit IA" },
      { id: "enrichment", label: "Enrichissement Client" },
    ]
  },
  {
    id: "connecteurs",
    label: "Connecteurs",
    icon: "📡",
    items: [
      { id: "connector-email", label: "Connecteur Email" },
      { id: "connector-sms", label: "Connecteur SMS" },
      { id: "connector-whatsapp", label: "Connecteur WhatsApp" },
      { id: "connector-sania", label: "Echanges SAN-IA" },
      { id: "connector-fournisseurs", label: "Connecteurs Fournisseurs" },
      { id: "conversations-history", label: "Historique Conversations" },
      { id: "pending-exchanges", label: "Echanges a rattacher" },
      { id: "ringover", label: "Administration Ringover" },
    ]
  },
  {
    id: "documents",
    label: "Documents & Projets",
    icon: "📁",
    items: [
      { id: "ged", label: "GED - Documents" },
      { id: "knowledge-center", label: "Centre de Connaissances" },
      { id: "project-management", label: "Gestion Projet" },
    ]
  },
  {
    id: "formations",
    label: "Formations",
    icon: "🎓",
    items: [
      { id: "formations-suivi", label: "Suivi Formations" },
    ]
  },
  {
    id: "automatisations",
    label: "Automatisations",
    icon: "⚡",
    items: [
      { id: "automations", label: "Automatisations" },
    ]
  },
];

const VISIBILITY_STATES = [
  { value: "visible", label: "Visible", icon: Eye, color: "emerald" },
  { value: "hidden", label: "Masque", icon: EyeOff, color: "red" },
  { value: "coming_soon", label: "Bientot dispo", icon: Clock, color: "amber" },
];

// Selecteur 3 etats pour un dashboard
function VisibilitySelector({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {VISIBILITY_STATES.map(({ value: v, label, icon: Icon, color }) => {
        const isActive = value === v;
        const colorMap = {
          emerald: isActive ? "bg-emerald-500/30 text-emerald-400 border-emerald-500/40" : "",
          red: isActive ? "bg-red-500/30 text-red-400 border-red-500/40" : "",
          amber: isActive ? "bg-amber-500/30 text-amber-400 border-amber-500/40" : "",
        };
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            title={label}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-all ${
              isActive
                ? colorMap[color]
                : "bg-slate-700/50 text-slate-500 border-transparent hover:bg-slate-700 hover:text-slate-300"
            }`}
          >
            <Icon className="w-3 h-3" />
            <span className="hidden xl:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function TenantPermissionsManager() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [showDefaults, setShowDefaults] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  // Dashboard visibility
  const [dashboardCatalog, setDashboardCatalog] = useState([]);
  const [dashboardVisibility, setDashboardVisibility] = useState({});
  const [defaultDashboardVisibility, setDefaultDashboardVisibility] = useState({});
  // Active tab: "super_batard" or "dashboards" or "integrations"
  const [activeTab, setActiveTab] = useState("dashboards");
  // Compteur visibilite par tenant (pre-charge)
  const [tenantVisibilitySummary, setTenantVisibilitySummary] = useState({});
  // Integration modes per tenant
  const [integrationModes, setIntegrationModes] = useState({});
  const [integrationsList, setIntegrationsList] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/billing/admin/overview`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setTenants(data.tenants || []);
      }
    } catch (e) {
      console.error("Erreur chargement tenants:", e);
    }
    setLoading(false);
  }, []);

  const loadDefaultPermissions = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/tenant-permissions/defaults`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDefaultPermissions(data.permissions || {});
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadDashboardCatalog = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/tenant-permissions/dashboard-catalog`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDashboardCatalog(data.dashboards || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadDefaultDashboardVisibility = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/tenant-permissions/dashboard-visibility-defaults`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setDefaultDashboardVisibility(data.visibility || {});
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadTenantPermissions = async (tenantId) => {
    try {
      const [permR, visR] = await Promise.all([
        authFetch(`${API}/admin/tenant-permissions/${tenantId}`, { headers: getHeaders() }),
        authFetch(`${API}/admin/tenant-permissions/${tenantId}/dashboard-visibility`, { headers: getHeaders() }),
      ]);
      if (permR.ok) {
        const data = await permR.json();
        setPermissions(data.permissions || {});
      }
      if (visR.ok) {
        const data = await visR.json();
        setDashboardVisibility(data.visibility || {});
      }
    } catch (e) { console.error(e); }
  };

  const loadTenantIntegrations = async (tenantId) => {
    setIntegrationsLoading(true);
    try {
      const r = await authFetch(`${API}/integrations/admin/tenant/${tenantId}`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setIntegrationsList(data.integrations || []);
        const modes = {};
        (data.integrations || []).forEach(i => { modes[i.id] = i.mode; });
        setIntegrationModes(modes);
      }
    } catch (e) { console.error("Erreur chargement integrations tenant:", e); }
    setIntegrationsLoading(false);
  };

  useEffect(() => {
    loadTenants();
    loadDefaultPermissions();
    loadDashboardCatalog();
    loadDefaultDashboardVisibility();
  }, [loadTenants, loadDefaultPermissions, loadDashboardCatalog, loadDefaultDashboardVisibility]);

  // Charger les compteurs de visibilite pour tous les tenants
  useEffect(() => {
    if (tenants.length === 0) return;
    const loadAllSummaries = async () => {
      const summaries = {};
      await Promise.all(tenants.map(async (t) => {
        try {
          const r = await authFetch(`${API}/admin/tenant-permissions/${t.tenant_id}/dashboard-visibility`, { headers: getHeaders() });
          if (r.ok) {
            const data = await r.json();
            const vis = data.visibility || {};
            const counts = { visible: 0, hidden: 0, coming_soon: 0 };
            Object.values(vis).forEach(v => { if (counts[v] !== undefined) counts[v]++; });
            summaries[t.tenant_id] = counts;
          }
        } catch (e) { /* ignore */ }
      }));
      setTenantVisibilitySummary(summaries);
    };
    loadAllSummaries();
  }, [tenants]);

  const handleSelectTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setShowDefaults(false);
    await Promise.all([
      loadTenantPermissions(tenant.tenant_id),
      loadTenantIntegrations(tenant.tenant_id),
    ]);
  };

  // === Super Batard Permissions ===
  const togglePermission = (itemId) => {
    setPermissions(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  const toggleSection = (section, enabled) => {
    const newPerms = { ...permissions, [section.id]: enabled };
    section.items.forEach(item => { newPerms[item.id] = enabled; });
    setPermissions(newPerms);
  };
  const toggleDefaultPermission = (itemId) => {
    setDefaultPermissions(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  const toggleDefaultSection = (section, enabled) => {
    const newPerms = { ...defaultPermissions, [section.id]: enabled };
    section.items.forEach(item => { newPerms[item.id] = enabled; });
    setDefaultPermissions(newPerms);
  };

  // === Dashboard Visibility ===
  const setDashVis = (key, state) => {
    setDashboardVisibility(prev => ({ ...prev, [key]: state }));
  };
  const setDefaultDashVis = (key, state) => {
    setDefaultDashboardVisibility(prev => ({ ...prev, [key]: state }));
  };
  const setAllDashVis = (state, isDefault) => {
    const setter = isDefault ? setDefaultDashboardVisibility : setDashboardVisibility;
    setter(prev => {
      const n = { ...prev };
      dashboardCatalog.forEach(d => { n[d.key] = state; });
      return n;
    });
  };

  // === Save ===
  const saveAll = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        authFetch(`${API}/admin/tenant-permissions/${selectedTenant.tenant_id}`, {
          method: "PUT", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ permissions }),
        }),
        authFetch(`${API}/admin/tenant-permissions/${selectedTenant.tenant_id}/dashboard-visibility`, {
          method: "PUT", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ visibility: dashboardVisibility }),
        }),
        authFetch(`${API}/integrations/admin/tenant/${selectedTenant.tenant_id}`, {
          method: "PUT", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ integrations: integrationModes }),
        }),
      ]);
      alert(r1.ok && r2.ok && r3.ok ? "Sauvegarde reussie !" : "Erreur lors de la sauvegarde");
      if (r1.ok && r2.ok) {
        const counts = { visible: 0, hidden: 0, coming_soon: 0 };
        Object.values(dashboardVisibility).forEach(v => { if (counts[v] !== undefined) counts[v]++; });
        setTenantVisibilitySummary(prev => ({ ...prev, [selectedTenant.tenant_id]: counts }));
      }
    } catch (e) { alert("Erreur: " + e.message); }
    setSaving(false);
  };

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      const [r1, r2] = await Promise.all([
        authFetch(`${API}/admin/tenant-permissions/defaults`, {
          method: "PUT", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ permissions: defaultPermissions }),
        }),
        authFetch(`${API}/admin/tenant-permissions/dashboard-visibility-defaults`, {
          method: "PUT", headers: { ...getHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ visibility: defaultDashboardVisibility }),
        }),
      ]);
      alert(r1.ok && r2.ok ? "Defauts sauvegardes !" : "Erreur lors de la sauvegarde");
    } catch (e) { alert("Erreur: " + e.message); }
    setSavingDefaults(false);
  };

  const applyDefaultsToTenant = () => {
    if (!selectedTenant) return;
    setPermissions({ ...defaultPermissions });
    setDashboardVisibility({ ...defaultDashboardVisibility });
  };

  // === Render Helpers ===
  const renderPermissionSection = (section, perms, togglePerm, toggleSec) => {
    const isExpanded = expandedSections[section.id];
    const allEnabled = section.items.every(item => perms[item.id]);
    const someEnabled = section.items.some(item => perms[item.id]);

    return (
      <div key={section.id} className="border border-slate-700 rounded-lg overflow-hidden">
        <div className="bg-slate-800/50 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
            className="flex items-center gap-3 flex-1"
          >
            <span className="text-lg">{section.icon}</span>
            <span className="font-medium text-white">{section.label}</span>
            <span className="text-xs text-slate-500">
              ({section.items.filter(i => perms[i.id]).length}/{section.items.length})
            </span>
            {isExpanded
              ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" />
              : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />
            }
          </button>
          <div className="flex items-center gap-2 ml-4">
            <button onClick={() => toggleSec(section, true)}
              className={`px-2 py-1 rounded text-xs transition-colors ${allEnabled ? "bg-emerald-500/30 text-emerald-400" : "bg-slate-700 text-slate-400 hover:bg-slate-600"}`}>
              <Eye className="w-3 h-3" />
            </button>
            <button onClick={() => toggleSec(section, false)}
              className={`px-2 py-1 rounded text-xs transition-colors ${!someEnabled ? "bg-red-500/30 text-red-400" : "bg-slate-700 text-slate-400 hover:bg-slate-600"}`}>
              <EyeOff className="w-3 h-3" />
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="px-4 py-2 space-y-1 bg-slate-900/30">
            {section.items.map(item => (
              <div key={item.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-slate-800/50">
                <span className="text-sm text-slate-300">{item.label}</span>
                <button onClick={() => togglePerm(item.id)}
                  className={`w-10 h-5 rounded-full transition-colors relative ${perms[item.id] ? "bg-emerald-500" : "bg-slate-600"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${perms[item.id] ? "left-5" : "left-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDashboardVisibility = (visibility, onChangeFn, isDefault) => (
    <div className="space-y-2">
      {/* Actions rapides */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-slate-500 mr-2">Actions rapides :</span>
        {VISIBILITY_STATES.map(({ value, label, icon: Icon, color }) => {
          const btnColor = { emerald: "hover:bg-emerald-500/20 hover:text-emerald-400", red: "hover:bg-red-500/20 hover:text-red-400", amber: "hover:bg-amber-500/20 hover:text-amber-400" };
          return (
            <button key={value} onClick={() => setAllDashVis(value, isDefault)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-slate-700/50 text-slate-400 transition-colors ${btnColor[color]}`}>
              <Icon className="w-3 h-3" /> Tout {label.toLowerCase()}
            </button>
          );
        })}
      </div>
      {/* Liste des dashboards */}
      <div className="grid grid-cols-1 gap-1.5">
        {dashboardCatalog.map(d => {
          const state = visibility[d.key] || "visible";
          return (
            <div key={d.key} data-testid={`dash-vis-${d.key}`}
              className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
                state === "visible" ? "bg-slate-800/30 border-slate-700/50" :
                state === "hidden" ? "bg-red-500/5 border-red-500/20 opacity-60" :
                "bg-amber-500/5 border-amber-500/20"
              }`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm">{d.label.split(" ")[0]}</span>
                <div className="min-w-0">
                  <span className="text-sm text-white font-medium">{d.label.replace(/^[^\s]+\s/, "")}</span>
                  <span className="text-xs text-slate-500 ml-2">{d.description}</span>
                </div>
              </div>
              <VisibilitySelector value={state} onChange={(v) => onChangeFn(d.key, v)} />
            </div>
          );
        })}
      </div>
    </div>
  );

  // Icon map for integrations
  const INTEG_ICON_MAP = {
    brain: Brain, phone: Phone, smartphone: Smartphone, "message-circle": MessageCircle,
    table: Table2, "hard-drive": HardDrive, mail: Mail, receipt: Receipt,
    palette: Palette, send: Send, "audio-lines": AudioLines, mic: Mic,
    video: Video, globe: Globe,
  };

  const MODE_OPTIONS = [
    { value: "platform", label: "Plateforme", color: "emerald", desc: "Utilise la cle partagee" },
    { value: "own_key", label: "Propre cle", color: "blue", desc: "Le tenant fournit sa cle" },
    { value: "disabled", label: "Desactive", color: "red", desc: "Non disponible" },
  ];

  const renderIntegrationModes = () => {
    if (integrationsLoading) {
      return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-slate-500" /></div>;
    }

    const platformCount = Object.values(integrationModes).filter(m => m === "platform" || !m).length;
    const ownCount = Object.values(integrationModes).filter(m => m === "own_key").length;
    const disabledCount = Object.values(integrationModes).filter(m => m === "disabled").length;

    // Quick action: set all to a mode
    const setAllIntegrations = (mode) => {
      const newModes = {};
      integrationsList.forEach(i => { newModes[i.id] = mode; });
      setIntegrationModes(newModes);
    };

    return (
      <div className="space-y-4">
        {/* Quick stats */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-500 mr-1">Actions rapides :</span>
          {MODE_OPTIONS.map(({ value, label, color }) => {
            const btnColors = {
              emerald: "hover:bg-emerald-500/20 hover:text-emerald-400",
              blue: "hover:bg-blue-500/20 hover:text-blue-400",
              red: "hover:bg-red-500/20 hover:text-red-400",
            };
            return (
              <button key={value} onClick={() => setAllIntegrations(value)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-slate-700/50 text-slate-400 transition-colors ${btnColors[color]}`}>
                Tout "{label}"
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-3 text-[11px]">
            <span className="text-emerald-400">{platformCount} plateforme</span>
            <span className="text-blue-400">{ownCount} propre</span>
            <span className="text-red-400">{disabledCount} desactive</span>
          </div>
        </div>

        {/* Integration list */}
        <div className="grid grid-cols-1 gap-1.5">
          {integrationsList.map(integ => {
            const mode = integrationModes[integ.id] || "platform";
            const IntegIcon = INTEG_ICON_MAP[integ.icon] || Key;
            return (
              <div key={integ.id} data-testid={`admin-integ-${integ.id}`}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                  mode === "platform" ? "bg-slate-800/30 border-slate-700/50" :
                  mode === "disabled" ? "bg-red-500/5 border-red-500/20 opacity-60" :
                  "bg-blue-500/5 border-blue-500/20"
                }`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${integ.color}18` }}>
                    <IntegIcon className="w-4 h-4" style={{ color: mode === "disabled" ? "#64748b" : integ.color }} />
                  </div>
                  <div className="min-w-0">
                    <span className={`text-sm font-medium ${mode === "disabled" ? "text-slate-500" : "text-white"}`}>
                      {integ.name}
                    </span>
                    <span className="text-xs text-slate-500 ml-2">{integ.description}</span>
                    {integ.has_own_key && mode === "own_key" && (
                      <span className="text-[10px] text-blue-400/70 ml-2">Cle: {integ.own_key_preview}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {MODE_OPTIONS.map(({ value, label, color }) => {
                    const isActive = mode === value;
                    const colorMap = {
                      emerald: isActive ? "bg-emerald-500/30 text-emerald-400 border-emerald-500/40" : "",
                      blue: isActive ? "bg-blue-500/30 text-blue-400 border-blue-500/40" : "",
                      red: isActive ? "bg-red-500/30 text-red-400 border-red-500/40" : "",
                    };
                    return (
                      <button key={value}
                        onClick={() => setIntegrationModes(prev => ({ ...prev, [integ.id]: value }))}
                        data-testid={`admin-mode-${value}-${integ.id}`}
                        className={`px-2 py-1 rounded text-[11px] border transition-all ${
                          isActive ? colorMap[color] : "bg-slate-700/50 text-slate-500 border-transparent hover:bg-slate-700 hover:text-slate-300"
                        }`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Onglets du panneau de config
  const renderTabs = () => (
    <div className="flex gap-1 mb-6 bg-slate-900/50 p-1 rounded-lg">
      <button onClick={() => setActiveTab("dashboards")} data-testid="tab-dashboards"
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          activeTab === "dashboards" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
        }`}>
        <Monitor className="w-4 h-4" /> Dashboards Home
      </button>
      <button onClick={() => setActiveTab("super_batard")} data-testid="tab-super-batard"
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          activeTab === "super_batard" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
        }`}>
        <Shield className="w-4 h-4" /> Super Batard
      </button>
      <button onClick={() => setActiveTab("integrations")} data-testid="tab-integrations"
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          activeTab === "integrations" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
        }`}>
        <Key className="w-4 h-4" /> Integrations
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-400" />
            Autorisations Tenants
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Gerez la visibilite des dashboards et les acces Super Batard par tenant
          </p>
        </div>
        <button onClick={loadTenants} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">Comment ca fonctionne :</p>
            <ul className="list-disc list-inside space-y-1 text-blue-300/80">
              <li><strong>Dashboards Home</strong> : controlez quels dashboards chaque tenant voit sur la page d'accueil (Visible / Masque / Bientot disponible)</li>
              <li><strong>Super Batard</strong> : controlez quelles sections du panneau admin chaque tenant peut utiliser</li>
              <li><strong>Integrations</strong> : definissez si le tenant utilise vos cles partagees, ses propres cles, ou si l'integration est desactivee</li>
              <li>Les <strong>valeurs par defaut</strong> s'appliquent automatiquement aux nouveaux tenants</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Liste des tenants */}
        <div className="col-span-1 space-y-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="font-medium text-white mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              Tenants ({tenants.length})
            </h3>
            <button onClick={() => { setShowDefaults(true); setSelectedTenant(null); }}
              className={`w-full mb-3 p-3 rounded-lg text-left transition-all ${
                showDefaults && !selectedTenant
                  ? "bg-amber-500/20 border border-amber-500/30"
                  : "bg-slate-700/50 hover:bg-slate-700 border border-transparent"
              }`}>
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400">Valeurs par defaut</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Appliquees aux nouveaux tenants</p>
            </button>
            <div className="border-t border-slate-700 pt-3 space-y-2 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-4"><RefreshCw className="w-6 h-6 animate-spin text-slate-500" /></div>
              ) : tenants.map(tenant => (
                <button key={tenant.tenant_id}
                  onClick={() => handleSelectTenant(tenant)}
                  className={`w-full p-3 rounded-lg text-left transition-all ${
                    selectedTenant?.tenant_id === tenant.tenant_id
                      ? "bg-blue-500/20 border border-blue-500/30"
                      : "bg-slate-700/50 hover:bg-slate-700 border border-transparent"
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white text-sm">{tenant.name || tenant.tenant_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      tenant.billing_status === "active"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-slate-600 text-slate-400"
                    }`}>{tenant.billing_status}</span>
                  </div>
                  {tenantVisibilitySummary[tenant.tenant_id] && (
                    <div className="flex gap-2 mt-1.5">
                      {tenantVisibilitySummary[tenant.tenant_id].visible > 0 && (
                        <span className="text-[10px] text-emerald-400/70">{tenantVisibilitySummary[tenant.tenant_id].visible} visible</span>
                      )}
                      {tenantVisibilitySummary[tenant.tenant_id].hidden > 0 && (
                        <span className="text-[10px] text-red-400/70">{tenantVisibilitySummary[tenant.tenant_id].hidden} masque</span>
                      )}
                      {tenantVisibilitySummary[tenant.tenant_id].coming_soon > 0 && (
                        <span className="text-[10px] text-amber-400/70">{tenantVisibilitySummary[tenant.tenant_id].coming_soon} bientot</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Panneau de configuration */}
        <div className="col-span-2">
          {showDefaults && !selectedTenant ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
                    <Settings className="w-5 h-5" /> Valeurs par defaut
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">Appliquees automatiquement a chaque nouveau tenant</p>
                </div>
                <button onClick={saveDefaults} disabled={savingDefaults}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg text-sm text-white">
                  {savingDefaults ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Sauvegarder
                </button>
              </div>
              {renderTabs()}
              {activeTab === "dashboards" ? (
                renderDashboardVisibility(defaultDashboardVisibility, setDefaultDashVis, true)
              ) : activeTab === "integrations" ? (
                <div className="text-center py-8 text-slate-500">
                  <Key className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                  <p className="text-sm">Selectionnez un tenant pour configurer ses integrations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {CONFIGURABLE_SECTIONS.map(s => renderPermissionSection(s, defaultPermissions, toggleDefaultPermission, toggleDefaultSection))}
                </div>
              )}
            </div>
          ) : selectedTenant ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    {selectedTenant.name || selectedTenant.tenant_id}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">Configurez les acces pour ce tenant</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={applyDefaultsToTenant}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white">
                    <Settings className="w-4 h-4" /> Appliquer defauts
                  </button>
                  <button onClick={saveAll} disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-sm text-white">
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Sauvegarder
                  </button>
                </div>
              </div>
              {renderTabs()}
              {activeTab === "dashboards" ? (
                renderDashboardVisibility(dashboardVisibility, setDashVis, false)
              ) : activeTab === "integrations" ? (
                renderIntegrationModes()
              ) : (
                <div className="space-y-3">
                  {CONFIGURABLE_SECTIONS.map(s => renderPermissionSection(s, permissions, togglePermission, toggleSection))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 flex flex-col items-center justify-center">
              <Shield className="w-16 h-16 text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-slate-400 mb-2">Selectionnez un tenant</h3>
              <p className="text-sm text-slate-500 text-center">
                Choisissez un tenant dans la liste pour configurer ses autorisations,<br />
                ou cliquez sur "Valeurs par defaut" pour configurer les nouveaux tenants.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
